/**
 * Markdown for report content: one parser, two outputs.
 *
 * `markdownToHtml` produces a string, for the clipboard and the print fallback. The screen
 * uses `shared/ui/Markdown`, which runs the same plugins through react-markdown and never
 * builds an HTML string at all.
 *
 * Both entry points clean their own input — callers pass raw `PromptReport.content`.
 */
import rehypeSanitize from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import type { Node } from "unist";
import { unified } from "unified";

type NodeWithChildren = Node & { children: Node[] };
type ImageNode = Node & { type: "image" | "imageReference"; alt?: string | null };

function hasChildren(node: Node): node is NodeWithChildren {
  return Array.isArray((node as Partial<NodeWithChildren>).children);
}

function isImageNode(node: Node): node is ImageNode {
  return node.type === "image" || node.type === "imageReference";
}

function toTextNode(alt: string | null | undefined): Node {
  const node = { type: "text" as const, value: alt ?? "" };
  return node;
}

/**
 * Rewrites every `image` and `imageReference` mdast node into a `text` node carrying the alt
 * value, walking the whole tree (a plain recursive function — no tree-walking dependency).
 *
 * An `<img>` survives `rehype-sanitize`'s default (GitHub) schema, and this app sets neither a
 * CSP nor a `Referrer-Policy`. Report content is LLM output generated from user-supplied
 * transcripts: a transcript carrying prompt-injection can make the model emit an image whose
 * URL encodes report text, and merely *viewing* the report — no click — would exfiltrate it
 * cross-origin. Rewriting the node before `remark-rehype` runs, rather than dropping `img` from
 * the sanitize schema, keeps the alt text on the page and matches
 * `api/services/docx_export/renderer.py`'s `case "image":`, which has always emitted alt text
 * only — one definition now covers the string pipeline below and `shared/ui/Markdown.tsx`.
 */
export function remarkImagesToText() {
  return (tree: Node): void => {
    walk(tree);
  };
}

function walk(node: Node): void {
  if (!hasChildren(node)) return;
  node.children = node.children.map((child) => (isImageNode(child) ? toTextNode(child.alt) : child));
  node.children.forEach(walk);
}

/**
 * Strip any leading machine-metadata block from report content. The model is
 * sometimes asked (or decides) to emit a YAML frontmatter block at the top of
 * a Summary — either bare (`---\n…\n---`) or wrapped in a ```yaml code fence.
 * It is indexing-only metadata and must never appear in the rendered view,
 * downloads, or clipboard copies. Safe to run on already-clean content.
 */
export function cleanReportContent(text: string): string {
  return text
    // Leading ```yaml … ``` fenced block (the fence usually wraps a --- block).
    .replace(/^\s*```ya?ml[ \t]*\n[\s\S]*?\n```[ \t]*\n?/, "")
    // Leading bare --- … --- YAML frontmatter block.
    .replace(/^\s*---[ \t]*\n[\s\S]*?\n---[ \t]*\n?/, "");
}

// remark-rehype drops raw HTML nodes by default (no allowDangerousHtml), which is why a
// <script> tag never reaches the output — that part needs no help from rehype-sanitize.
// What rehype-sanitize actually earns its place for is link schemes: remark-rehype has no
// equivalent of react-markdown's defaultUrlTransform, so without it this same pipeline emits
// <a href="javascript:alert(1)">. That matters here because this output reaches the
// clipboard and document.write in the print fallback, not just the screen.
// remarkBreaks keeps a single newline as a line break. Without it CommonMark merges a
// transcript's speaker label into its utterance — they are consecutive lines with no blank
// line between them.
const pipeline = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkBreaks)
  .use(remarkImagesToText)
  .use(remarkRehype)
  .use(rehypeSanitize)
  .use(rehypeStringify);

export function markdownToHtml(content: string): string {
  return String(pipeline.processSync(cleanReportContent(content)));
}
